import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Trophy, Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { WidgetLoading } from '@/features/dashboard/infrastructure/widgets/WidgetStates';
import { formatINRPaise } from '@/shared/utils/currency';

interface LoyaltyProgram {
  name: string;
  pointsPerHundred: number;
  expiryDays: number;
  pointValueMinor: number;
  minRedeemPoints: number;
  maxRedeemPercentage: number;
  isActive: boolean;
}

interface LoyaltyBalance {
  _id: string;
  customerId: string;
  customerName: string;
  currentBalance: number;
  totalEarned: number;
  totalRedeemed: number;
  totalExpired: number;
  lastEarnedAt: string | null;
}

function useProgram() {
  return useQuery({ queryKey: ['loyalty', 'program'], queryFn: async () => (await api.get<{ program: LoyaltyProgram | null }>('/api/loyalty/program')).data.program });
}

function useLeaderboard() {
  return useQuery({ queryKey: ['loyalty', 'leaderboard'], queryFn: async () => (await api.get<{ items: LoyaltyBalance[] }>('/api/loyalty/leaderboard')).data.items });
}

export function LoyaltyPage() {
  const qc = useQueryClient();
  const { data: program, isLoading: loadingProgram } = useProgram();
  const { data: leaderboard = [], isLoading: loadingBoard } = useLeaderboard();
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState({ name: '', pointsPerHundred: '1', expiryDays: '365', pointValueMinor: '100', minRedeemPoints: '100', maxRedeemPercentage: '50' });

  const saveProgram = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.put('/api/loyalty/program', body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['loyalty'] }); toast.success('Loyalty program saved.'); setShowConfig(false); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed.'),
  });

  const openConfig = () => {
    if (program) {
      setForm({
        name: program.name || '', pointsPerHundred: String(program.pointsPerHundred || 1),
        expiryDays: String(program.expiryDays || 365), pointValueMinor: String(program.pointValueMinor || 100),
        minRedeemPoints: String(program.minRedeemPoints || 100), maxRedeemPercentage: String(program.maxRedeemPercentage || 50),
      });
    }
    setShowConfig(true);
  };

  const handleSave = () => {
    saveProgram.mutate({
      name: form.name || 'RBM Loyalty Program', isActive: true,
      pointsPerHundred: Number(form.pointsPerHundred) || 1, expiryDays: Number(form.expiryDays) || 365,
      pointValueMinor: Number(form.pointValueMinor) || 100, minRedeemPoints: Number(form.minRedeemPoints) || 100,
      maxRedeemPercentage: Number(form.maxRedeemPercentage) || 50,
    });
  };

  return (
    <div className="space-y-6 max-w-[1580px] mx-auto pb-16">
      <PageHeader icon={Star} eyebrow="Marketing" title="Loyalty Points" description={<>Customer reward points — earn on invoices, redeem on orders.</>} actions={<><Button variant="outline" onClick={openConfig}><Settings className="h-4 w-4" /> Configure</Button></>} />

      {/* Program Summary */}
      {loadingProgram ? <div className="grid gap-4 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-[86px] animate-pulse rounded-card border border-border bg-card" />)}</div> : program ? (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="shadow-xs"><CardBody className="flex flex-col gap-1"><p className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{program.pointsPerHundred} pt / ₹100</p><p className="text-body-sm text-on-surface-variant">Earn Rate</p></CardBody></Card>
          <Card className="shadow-xs"><CardBody className="flex flex-col gap-1"><p className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{formatINRPaise(program.pointValueMinor / 100)}</p><p className="text-body-sm text-on-surface-variant">Point Value</p></CardBody></Card>
          <Card className="shadow-xs"><CardBody className="flex flex-col gap-1"><p className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{program.expiryDays} days</p><p className="text-body-sm text-on-surface-variant">Expiry</p></CardBody></Card>
          <Card className="shadow-xs"><CardBody className="flex flex-col gap-1"><p className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{program.maxRedeemPercentage}% of order</p><p className="text-body-sm text-on-surface-variant">Max Redeem</p></CardBody></Card>
        </div>
      ) : (
        <Card><CardBody className="text-center py-8"><p className="text-on-surface-variant">No loyalty program configured yet.</p><Button onClick={openConfig} className="mt-3">Set Up Program</Button></CardBody></Card>
      )}

      {/* Leaderboard */}
      <Card>
        <CardHeader title="Customer Points Leaderboard" subtitle="Top customers by current balance" action={<Trophy className="h-5 w-5 text-warning-text" />} />
        <CardBody className="p-0">
          {loadingBoard ? <div className="p-4"><WidgetLoading variant="table" /></div> : leaderboard.length === 0 ? (
            <p className="p-8 text-center text-body-sm text-on-surface-variant">No loyalty balances yet. Points are earned when invoices are posted.</p>
          ) : (
            <table className="w-full text-body-md text-left">
              <thead className="border-b border-border bg-muted text-label-caps uppercase tracking-wider text-on-surface-variant"><tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3 text-right">Earned</th><th className="px-4 py-3 text-right">Redeemed</th><th className="px-4 py-3">Last Earned</th></tr></thead>
              <tbody className="divide-y divide-border">{leaderboard.map((b, i) => (
                <tr key={b._id} className="hover:bg-surface-variant/30">
                  <td className="px-4 py-3 font-bold text-on-surface-variant">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-on-surface">{b.customerName || 'Customer'}</td>
                  <td className="font-mono px-4 py-3 text-right font-bold text-primary tabular-nums text-data-sm">{b.currentBalance.toLocaleString()}</td>
                  <td className="font-mono px-4 py-3 text-right text-on-surface-variant tabular-nums text-data-sm">{b.totalEarned.toLocaleString()}</td>
                  <td className="font-mono px-4 py-3 text-right text-on-surface-variant tabular-nums text-data-sm">{b.totalRedeemed.toLocaleString()}</td>
                  <td className="px-4 py-3 text-on-surface-variant">{b.lastEarnedAt ? new Date(b.lastEarnedAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Config Modal */}
      <Modal open={showConfig} onClose={() => setShowConfig(false)} title="Loyalty Program Configuration" footer={<><Button variant="ghost" onClick={() => setShowConfig(false)}>Cancel</Button><Button onClick={handleSave} loading={saveProgram.isPending}>Save</Button></>}>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2"><Input label="Program Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="RBM Loyalty Program" /></div>
          <Input label="Points per ₹100 spent" type="number" value={form.pointsPerHundred} onChange={e => setForm(f => ({ ...f, pointsPerHundred: e.target.value }))} />
          <Input label="Point value (paise)" type="number" value={form.pointValueMinor} onChange={e => setForm(f => ({ ...f, pointValueMinor: e.target.value }))} />
          <Input label="Expiry (days, 0=never)" type="number" value={form.expiryDays} onChange={e => setForm(f => ({ ...f, expiryDays: e.target.value }))} />
          <Input label="Min points to redeem" type="number" value={form.minRedeemPoints} onChange={e => setForm(f => ({ ...f, minRedeemPoints: e.target.value }))} />
          <Input label="Max % of order redeemable" type="number" value={form.maxRedeemPercentage} onChange={e => setForm(f => ({ ...f, maxRedeemPercentage: e.target.value }))} />
        </div>
      </Modal>
    </div>
  );
}
